import { ItemModel } from 'src/app/models/itemModel';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, distinctUntilChanged, filter, map, shareReplay, startWith } from 'rxjs';
import {
  ActionsQueryParamKeys,
  checkIfQueryParamSetToFalse,
  checkIfQueryParamSetToTrue,
  isRoleplayLikeSession,
} from '../common/utils/actions-query-params-util';
import { URL_CONSTANTS } from '../common/utils/url';

/** Single source of truth for the dedicated Luna experience. */
@Injectable({ providedIn: 'root' })
export class AiTutorModeService {
  readonly enabled$: Observable<boolean> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.enabled),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly workspaceEnabled$: Observable<boolean> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.workspaceEnabled),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(private readonly router: Router) {}

  /**
   * Luna workspace mode (tabs, full-screen apps, vertical whiteboard): on by default wherever
   * AI-tutor mode is on; `?lunaworkspace=false` brings back the old view.
   */
  get workspaceEnabled(): boolean {
    return this.enabled && !checkIfQueryParamSetToFalse(ActionsQueryParamKeys.LUNA_WORKSPACE);
  }

  /**
   * Read live because this service may exist before token-login reaches the space URL.
   *
   * On by default in every space, so no `?aitutor=true` is needed; `?aitutor=false` opts
   * out. Roleplay/persona sessions keep their normal VCT view unless `?aitutor=true` is
   * passed explicitly. Off outside spaces so the rest of the app keeps its navigation.
   */
  /**
   * Whether this board item may not be deleted by the people in the space: in a Luna session
   * the apps (Mario items) are Luna's, so nobody can close or delete one from the board.
   */
  isProtectedItem(itemType: unknown): boolean {
    return this.enabled && itemType === ItemModel.Mario;
  }

  get enabled(): boolean {
    if (checkIfQueryParamSetToTrue(ActionsQueryParamKeys.AI_TUTOR)) {
      return true;
    }
    return (
      location.pathname.includes(`/${URL_CONSTANTS.SPACES}/`) &&
      !checkIfQueryParamSetToFalse(ActionsQueryParamKeys.AI_TUTOR) &&
      !isRoleplayLikeSession()
    );
  }
}
